/* import __COLOCATED_TEMPLATE__ from './restart-subscription-modal.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import { dropTask } from 'ember-concurrency-decorators';
import type Store from '@ember-data/store';
import { captureException } from 'embercom/lib/sentry';
import type IntlService from 'ember-intl/services/intl';
import { taskFor } from 'ember-concurrency-ts';
import moment from 'moment-timezone';
import { post } from 'embercom/lib/ajax';

interface Signature {
  Args: {
    isOpen: boolean;
    onModalClose: () => void;
  };
}

export default class RestartSubscriptionModal extends Component<Signature> {
  @service appService: any;
  @service intercomEventService: any;
  @service declare router: RouterService;
  @service declare store: Store;
  @service declare customerService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;

  get isOpen() {
    return this.args.isOpen;
  }

  get modalDescriptionKey() {
    return this.intl.t('billing.settings.restart-subscription-modal.description', {
      billingDate: this.nextInvoiceDate,
    });
  }

  get app() {
    return this.appService.app;
  }

  get nextInvoiceDate() {
    return moment(this.customerService.customer.subscription.currentCycleDate.to).format('ll');
  }

  get isRestarting() {
    return taskFor(this.restartSubscriptionTask).isRunning;
  }

  @action
  async handleRestartSubscription() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'continue_to_restart_subscription',
      section: 'restart_subscription_modal',
      place: 'billing_settings',
    });

    await taskFor(this.restartSubscriptionTask).perform();
    this.args.onModalClose();
    this.router.transitionTo('apps.app.settings.subscription.billing.settings', {
      queryParams: {
        redirect_source: null,
      },
    });
  }

  @action
  handleModalClose(object: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object,
      section: 'restart_subscription_modal',
      place: 'billing_settings',
      context: 'abort_restart_subscription',
    });
    this.args.onModalClose();
  }

  @dropTask *restartSubscriptionTask(): Generator<Promise<void>> {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'restart_subscription_button',
      place: 'settings.subscription.billing.cancel.restart-subscription-modal',
      section: 'restart_subscription_modal',
      context: 'billing_settings',
    });
    try {
      yield post(
        `/ember/customers/${this.customerService.customer.id}/resume_pending_cancellation`,
        {
          app_id: this.app.id,
        },
      );
      this.notificationsService.notifyConfirmation(
        this.intl.t('billing.settings.restart-subscription-modal.success-notification'),
      );
      taskFor(this.customerService.fetchCustomerTask).perform(this.customerService.customer.id);
    } catch (err) {
      this.notificationsService.notifyError(
        this.intl.t('billing.settings.restart-subscription-modal.error-notification'),
      );
      captureException(err, {
        'responsible-team': 'team-self-serve',
      });
      throw err;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Billing::Settings::Cancel::RestartSubscriptionModal': typeof RestartSubscriptionModal;
    'billing/settings/cancel/restart-subscription-modal': typeof RestartSubscriptionModal;
  }
}
